// Appels réseau des relances : déclarations « J'ai déjà réglé » et liste
// « À appeler ». Un seul fichier pour tous les écrans (contrat d'API commun
// avec le serveur, 30/09/2026).
//
// Côté responsable : apiClient (connexion, et organization_id ajouté tout seul
// quand l'owner consulte une synagogue). Côté donateur : axios nu sur la route
// publique du lien de paiement (/pay/{token}), comme le reste de PayPage.
import axios from "axios";
import { apiClient, PUBLIC_API } from "./api";

// ── Responsable (admin / manager de la synagogue, ou owner) ────────────────
export async function listerDeclarations() {
  const res = await apiClient.get("/pledges/declarations", { params: { status: "pending" } });
  return normaliserListe(res.data);
}

// « Confirmer le paiement » = EXACTEMENT « Marquer payé » : mêmes cases
// cochées d'office que la modale (reçu Cerfa créé ET envoyé par email).
// `echeance` (promesse en plusieurs fois) : seul ce montant est enregistré ;
// absent → tout le reste dû.
export async function confirmerDeclaration(pledgeId, { echeance } = {}) {
  const corps = { generate_cerfa: true, send_cerfa_email: true };
  if (echeance) corps.amount = echeance;
  const res = await apiClient.post(`/pledges/${pledgeId}/declaration/confirm`, corps);
  return res.data;
}

export async function refuserDeclaration(pledgeId) {
  const res = await apiClient.post(`/pledges/${pledgeId}/declaration/refuse`);
  return res.data;
}

export async function listerAAppeler() {
  const res = await apiClient.get("/pledges/to-call");
  return normaliserListe(res.data);
}

// `pourLeDonateur` : toutes ses promesses « À appeler » reprennent ensemble
// (liste « À appeler », une carte par donateur).
export async function reprendreRelancesAuto(pledgeId, { pourLeDonateur = false } = {}) {
  const res = await apiClient.post(
    `/pledges/${pledgeId}/to-call/resume`,
    null,
    pourLeDonateur ? { params: { for_donor: true } } : undefined,
  );
  return res.data;
}

// Relance manuelle (bouton existant) — canal choisi par le serveur selon les
// réglages ; la lecture du résultat passe par lib/reminderOutcome.
export async function relancerMaintenant(pledgeId) {
  const res = await apiClient.post(`/pledges/${pledgeId}/magic-link/send`);
  return res.data;
}

// « Relancer quand même » depuis la liste « À appeler » : UN message qui
// cite toutes les promesses « À appeler » du donateur (même réponse que la
// relance manuelle, lue par lib/reminderOutcome).
export async function relancerDonateurAAppeler(pledgeId) {
  const res = await apiClient.post(`/pledges/${pledgeId}/to-call/remind`);
  return res.data;
}

// Écran Réglages → Relances : ce que le rythme PROPOSÉ (pas encore
// enregistré) changerait au prochain envoi. Rien n'est envoyé ni écrit.
// `reglage` : { reminder_rhythm, reminder_custom_… } (cf. lib/relances
// champsRythme). Réponse lue par lib/relances phraseApercuRythme.
export async function apercuRythme(orgId, reglage, { signal } = {}) {
  const res = await apiClient.post(`/organizations/${orgId}/reminders/rhythm-preview`, reglage, { signal });
  return res.data;
}

// ── Donateur (page publique du lien de paiement) ───────────────────────────
// Corps : { method: "especes|cheque|virement|autre", paid_on: "YYYY-MM-DD"|null,
//          note: "…"|null, pledge_ids?: [...] }
// pledge_ids (facultatif) : promesses cochées sur la page ; absent → celle(s)
// du lien, choisies par le serveur.
export async function declarerDejaRegle(token, { method, paid_on, note, pledge_ids }) {
  const corps = {
    method,
    paid_on: paid_on || null,
    note: note && note.trim() ? note.trim() : null,
  };
  if (Array.isArray(pledge_ids) && pledge_ids.length) corps.pledge_ids = pledge_ids;
  const res = await axios.post(`${PUBLIC_API}/pay/${token}/declare-paid`, corps);
  return res.data;
}

// { items, count } toujours bien formé, même si le serveur renvoie autre chose.
function normaliserListe(data) {
  const items = Array.isArray(data?.items) ? data.items : [];
  const count = typeof data?.count === "number" ? data.count : items.length;
  return { items, count };
}

// Message d'erreur lisible pour un responsable (jamais de jargon).
export function messageErreur(err, repli = "L'action n'a pas abouti. Réessayez.") {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 409) return typeof detail === "string" ? detail : "Déjà traité par un autre responsable.";
  if (status === 404) return "Cette promesse n'est plus disponible.";
  if (status === 429) return "Trop de tentatives d'un coup. Patientez une minute puis réessayez.";
  if (!err?.response) return "Pas de connexion internet pour le moment. Réessayez.";
  return typeof detail === "string" && detail ? detail : repli;
}
